import { getAcombagoOrigins } from './portalConfig';
import { useTransphereStatusStore } from '../../stores/transphereStatusStore';

// True only when ComptaAI is actually running inside the AcombaGO host iframe — the one context
// where the portal:* postMessages below are delivered and handled. This is the live source of
// truth for "a drilldown can work right now". Note it is independent of the persisted
// sessionStore.sessionKind, which can stay 'portal' after reopening standalone and must not be
// used on its own to decide whether a drilldown link is actionable.
export function isEmbeddedInPortal(): boolean {
    return window.parent !== window;
}

// Posts a portal:* message to the AcombaGO host. No-op when not embedded (standalone dev) — there
// is no AcombaGO parent to handle it. The browser only delivers to the parent whose origin matches,
// so posting to each configured AcombaGO origin is safe and avoids a wildcard target.
function postToPortal(message: { type: string; [key: string]: unknown }): void {
    if (!isEmbeddedInPortal()) return;
    for (const origin of getAcombagoOrigins()) {
        window.parent.postMessage(message, origin);
    }
}

// Asks the AcombaGO host to open a customer's record in its own tab system. `customerExternalId`
// is the Acomba customer id (ComptaAI's CustomerProfile.ExternalId).
export function openCustomerInPortal(customerExternalId: number | null): void {
    if (customerExternalId == null) return;
    postToPortal({ type: 'portal:open-customer', customerId: customerExternalId });
}

// Asks the AcombaGO host to open a Payment window pre-filled with the customer and the amount to
// collect. `customerId` is the Acomba customer id (ComptaAI's CustomerProfile.ExternalId);
// `amountReceived` is the relance's past-due total, passed on whatever its sign — the host drops
// anything not strictly positive.
export function openPaymentInPortal(customerId: number | null, amountReceived: number): void {
    if (customerId == null) return;
    postToPortal({ type: 'portal:open-payment', customerId, amountReceived });
}

// Asks the AcombaGO host to open an invoice. `transactionReference` is the Acomba NoAutoTransaction
// (RelanceFlagsDto.paymentTermGapTransactions).
export function openTransactionInPortal(transactionReference: string | null): void {
    if (!transactionReference) return;
    postToPortal({ type: 'portal:open-transaction', transactionId: transactionReference });
}

// Asks the AcombaGO host to open a customer inscription (account entry). `registrationReference`
// is the Acomba NoAutoInscriptionClient.
export function openRegistrationInPortal(registrationReference: string | null): void {
    if (!registrationReference) return;
    postToPortal({ type: 'portal:open-registration', registrationId: registrationReference });
}

// Asks the AcombaGO host to open the customer encaissement (ACCEO Transphere collection) window,
// pre-filtered to the date range that scopes the unapplied payments (oldest pending payment → today).
// Both dates are ISO 'yyyy-MM-dd' strings carried by the reliability indicator.
export function openCustomerEntryInPortal(fromDate: string, toDate: string): void {
    if (!fromDate || !toDate) return;
    postToPortal({ type: 'portal:open-customer-entry', fromDate, toDate });
}

// Asks the AcombaGO host to open the "invoices pending send to Transphere" window. Carries no id: the
// window lists everything queued. It does need a param object all the same — AcombaGO only runs its
// search on first show when one is present, and it reads customerId off it, so the fields are sent
// explicitly null rather than omitted.
export function openTransphereInvoicesInPortal(): void {
    postToPortal({ type: 'portal:open-transphere-invoices', customerId: null, customerNumber: null, customerName: null });
}

// Asks the AcombaGO host to open the Configuration compagnie window on the ACCEO Transphere tab —
// the encaissement subscription page when the tenant already holds a Transphere subscription, or
// the initial activation page otherwise. No id payload; the target page is entirely determined by
// `target`. Like the other helpers here, this is a no-op outside the AcombaGO iframe.
// Arming the re-read lives here, not at the call sites: two screens deep-link into this one.
export type TransphereConfigTarget = 'activate' | 'subscribe-encaissement';

export function openTransphereConfigInPortal(target: TransphereConfigTarget): void {
    if (!isEmbeddedInPortal()) return;

    useTransphereStatusStore.getState().expectChange();
    postToPortal({ type: 'portal:open-transphere-configuration', target });
}

const TRANSPHERE_MARKETING_URL = 'https://www.acceotransphere.com/';

// Opens the ACCEO Transphere product site in a new tab
export function openTransphereMarketingSite(): void {
    window.open(TRANSPHERE_MARKETING_URL, '_blank', 'noopener,noreferrer');
}
